import React from 'react';
import type { TTimesheet, TApprovalErrorType } from '../../../types/timesheet';
import TimesheetList from './TimesheetList';
import ErrorPage from './ErrorPage';

type PageState = 'loading' | 'loaded' | 'error';

type TimesheetApprovalContentProps = {
    pageState: PageState;
    timesheets: TTimesheet[];
    clientName?: string;
    errorType: TApprovalErrorType;
    errorMessage: string;
    onApprove?: (timesheetIds: number[]) => Promise<void>;
    onReject?: (timesheetId: number, reason: string) => Promise<void>;
    onRetry?: () => void;
    showErrorPage?: boolean;
    skipApprovalModal?: boolean;
};

const TimesheetApprovalContent: React.FC<TimesheetApprovalContentProps> = ({
    pageState,
    timesheets,
    clientName,
    errorType,
    errorMessage,
    onApprove,
    onReject,
    onRetry,
    showErrorPage = true,
    skipApprovalModal = false,
}) => {
    if (pageState === 'loading') {
        return (
            <div className="loading-container">
                <div className="loading-spinner" />
                <p>Loading timesheets...</p>
            </div>
        );
    }

    if (pageState === 'error') {
        if (showErrorPage) {
            return (
                <ErrorPage
                    errorType={errorType}
                    message={errorMessage}
                    onRetry={errorType === 'network' && onRetry ? onRetry : undefined}
                />
            );
        }

        return (
            <div className="client-timesheet-error">
                <div className="client-timesheet-error__icon">
                    <i className="icon icon-warning" />
                </div>
                <h2 className="client-timesheet-error__title">
                    {errorType === 'network' ? 'Connection Error' : 'Error Loading Timesheets'}
                </h2>
                <p className="client-timesheet-error__message">{errorMessage}</p>
                {errorType === 'network' && onRetry && (
                    <button
                        type="button"
                        className="table-action-button"
                        onClick={onRetry}
                    >
                        Try Again
                    </button>
                )}
            </div>
        );
    }

    if (timesheets.length === 0) {
        return (
            <div className="client-timesheet-empty">
                <div className="client-timesheet-empty__icon">
                    <i className="icon icon-check-circle" />
                </div>
                <h2 className="client-timesheet-empty__title">All caught up!</h2>
                <p className="client-timesheet-empty__message">
                    There are no timesheets awaiting your approval.
                </p>
            </div>
        );
    }

    return (
        <>
            <div className="approval-page__header">
                <h1 className="approval-page__page-title">Timesheets Awaiting Approval</h1>
                {clientName && (
                    <p className="approval-page__welcome-message">
                        Welcome, {clientName}
                    </p>
                )}
                <p className="approval-page__description">
                    Please review the timesheets below. You can approve individual timesheets by clicking the &quot;Approve&quot; button,
                    or select multiple timesheets using the checkboxes and approve them all at once using the &quot;Approve selected Timesheets&quot;
                    button at the bottom. If you need to reject a timesheet, please click &quot;Reject&quot; and provide a reason.
                </p>
            </div>

            <div className="approval-page__table-container">
                {onApprove && onReject && (
                    <TimesheetList
                        timesheets={timesheets}
                        onApprove={onApprove}
                        onReject={onReject}
                        skipApprovalModal={skipApprovalModal}
                    />
                )}
            </div>
        </>
    );
};

export default TimesheetApprovalContent;
